import AssignmentIcon from "@mui/icons-material/Assignment";
import GroupIcon from "@mui/icons-material/Group";
import { Grid, Paper, styled, Typography } from "@mui/material";
import { api, types } from "api";
import crown from "assets/icons/crown.svg";
import { AssignmentModelList } from "components/AssignmentModelList";
import { GroupList } from "components/GroupList/GroupList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { StyledButton } from "components/StyledButton";
import { Assignment, getStatus } from "helpers/assignment";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "HomeDashboardPage";

const classes = {
	root: `${PREFIX}-root`,
	button: `${PREFIX}-button`,
	title: `${PREFIX}-title`,
	groupList: `${PREFIX}-groupList`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},

	[`& .${classes.button}`]: {
		marginTop: 15,
		marginRight: 5,
		float: "right",
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.groupList}`]: {
		maxHeight: 200,
		padding: 5,
		overflowY: "auto",
		overflowX: "hidden",
		float: "left",
	},

	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		height: 200,
		padding: theme.spacing(2),
		minWidth: 200,
		marginRight: theme.spacing(4),
	},

	[`& .${classes.summaryTitle}`]: {
		color: theme.palette.text.secondary,
		textAlign: "center",
		fontSize: 16,
		paddingTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.summaryText}`]: {
		fontSize: 16,
		textAlign: "center",
		fontWeight: "bold",
		marginTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.icon}`]: {
		color: theme.palette.warning.main,
	},
}));

export const HomeDashboardPage = (): JSX.Element => {
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const [groups, setGroups] = useState<types.Group[]>([]);
	const [err, setErr] = useState(false);
	const [name, setName] = useState("");
	const [role, setRole] = useState("");
	const [studentsNum, setStudentsNum] = useState(0);

	const componentMounted = useRef(true);
	const history = useHistory();

	const showRole = (roles: types.Roles): void => {
		if (roles.includes("admin")) {
			setRole("Admin");
			return;
		}
		if (roles.includes("teacher")) {
			setRole("Teacher");
			return;
		}
	};

	useEffect(() => {
		api
			.getMyUserData()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					setName(res.data.firstName);
					showRole(res.data.role);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getUsers({ role: "student" })
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current && res.data) {
					setStudentsNum(res.data.length);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getAssignments()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignment(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getGroups()
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					setGroups(res.data || []);
				}
			})
			.catch(() => {
				setErr(true);
			});
		return () => {
			componentMounted.current = false;
		};
	}, []);

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography variant="h2" data-testid="hello">
						Hi {name} 👋
					</Typography>
					<Typography variant="h5">
						Welcome back to Science Island. Here’s a summary of what you’ve
						missed!
					</Typography>
				</Grid>
				<Grid item xs={5}>
					<Grid container spacing={2}>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>ROLE</Typography>
							<Typography className={classes.summaryText}>{role}</Typography>
							<Grid container justifyContent="space-around">
								<img
									data-testid="crown"
									src={crown}
									alt="crown"
									style={{ maxHeight: 60, maxWidth: 60 }}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								TOTAL STUDENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{studentsNum} students
							</Typography>
							<Grid container justifyContent="space-around">
								<GroupIcon
									data-testid="students"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								TOTAL ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{assignments.length} assignments
							</Typography>
							<Grid container justifyContent="space-around">
								<AssignmentIcon
									data-testid="assignments"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
					</Grid>
				</Grid>
				<Grid item xs={7}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography className={classes.title} variant="h4">
								Groups
							</Typography>
							<StyledButton
								variant="contained"
								color="primary"
								className={classes.button}
								onClick={() => {
									history.push(routes._.groups._.create.path);
								}}
								data-testid="create-group-btn"
							>
								ADD GROUP
							</StyledButton>
						</Grid>
						<Grid item xs={12} className={classes.groupList}>
							{groups.length === 0 ? (
								<Typography variant="h5" className={classes.title}>
									No groups
								</Typography>
							) : (
								<GroupList groups={groups} />
							)}
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Typography className={classes.title} variant="h4">
						Assignments
					</Typography>
					<StyledButton
						variant="contained"
						color="primary"
						className={classes.button}
						onClick={() => {
							history.push(routes._.assignments._.create.path);
						}}
						data-testid="create-button"
					>
						CREATE
					</StyledButton>
				</Grid>

				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={false} />
					)}
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
